import * as signalR from "@microsoft/signalr";
import { unwrapResult } from '@reduxjs/toolkit';
import * as React from 'react';
import { Col, Row, Spinner, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import { Afr, deleteDocument, DocumentListEntry, DocumentReferenceInput, getSelectedAfr, loadAfr, pullDocumentList, setAuditReceivedDate } from '../../redux/AfrSlice';
import { AFRDocumentStarter, DocumentNotification, downloadDocumentById, getAFRReport, updateDocumentNotification, uploadAuditDocument } from '../../redux/DocumentsSlice';
import { Entity, getSelectedEntity, getNameForAfr } from "../../redux/EntitySlice";
import { setIsDirty } from '../../redux/MiscSlice';
import { TaxonomyTypeEnum } from "../../redux/TaxonomyVersionSlice";
import { UserRoleEnum, selectUserRole } from "../../redux/UserSlice";
import dateHelper from '../../utils/dateHelper';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import { GenericFileUploader } from "../core/GenericFileUploader";
import AdminDocumentUploader from "./AdminDocumentUploader";
import ReportDataImporter from "./ReportDataImporter";


const DocumentList = function (props: {
    userRole: string,
    taxonomyType: TaxonomyTypeEnum | null,
    importLabel: string,
    downloadTemplateLabel: string
   }) {

    const dispatch = useAppDispatch();
    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const documentList: Array<DocumentListEntry> = useAppSelector(pullDocumentList);
    const entity: Entity | null = useAppSelector(getSelectedEntity);
    const userRoleSelector = useAppSelector(selectUserRole);

    const [documentListItemIsLoading, setSelectedDocumentListItemIsLoading] = React.useState<boolean>(false);
    const [selectedDocumentListItem, setSelectedDocumentListItem] = React.useState<string>("");
    const [afrXhtmlDownloadIsInProgress, setAfrXhtmlDownloadIsInProgress] = React.useState<boolean>(false);
    const [afrPdfDownloadIsInProgress, setAfrPdfDownloadIsInProgress] = React.useState<boolean>(false);
    const [spfsPdfDownloadIsInProgress, setSpfsPdfDownloadIsInProgress] = React.useState<boolean>(false);
    const [spfsXhtmlDownloadIsInProgress, setSpfsXhtmlDownloadIsInProgress] = React.useState<boolean>(false);

   
    const handleClickDownload = (docId: string | null) => async (event: any) => {
        if (docId) {
            setSelectedDocumentListItem(docId);
            setSelectedDocumentListItemIsLoading(true);
            await dispatch(downloadDocumentById(docId));
            setSelectedDocumentListItemIsLoading(false);
            setSelectedDocumentListItem("");
        }
    }

    const handleClickDelete = (docId: string | null, role: string, affiliate: string | null) => async (event: any) => {
        if (docId) {
            var document: DocumentReferenceInput = { documentId: docId, role: role, affiliate: affiliate }

            dispatch(deleteDocument(document));
            switch (role.toLowerCase()) {
                case "audit":
                    if (!affiliate) {
                        dispatch(setAuditReceivedDate(null));
                    }
                    break;

                default:
                    break;
            }
            // Turn ON the dirty flag in redux
            dispatch(setIsDirty({ IsDirty: true, Page: "" }));
        }
    }

    const getReportedName = function(option: any): string {
        if (utilityHelper.isNullDocumentRelationship(option.document)) {
            return "No document will be supplied";
        }
        return option.name;
    }

    const hideDocUploader = function (): boolean {
        if (!userRoleSelector) {
            return true;
        }
        if (!currentAfr) {
            return true;
        }
        if (currentAfr.status === "VERIFIED") {
            return true;
        }
        var result: boolean = false;
        switch (props.taxonomyType) {
            case TaxonomyTypeEnum.COURT_REPORTING_939:
                result = true;
                break;

            case TaxonomyTypeEnum.COURT_REPORTING_29:
                result = false;
                break;

            case TaxonomyTypeEnum.AFR:
                if (userRoleSelector !== UserRoleEnum.Admin) {
                    result = true;
                }
                break;
                
        }
        return result;
    }

    const getFinancialReportName = function(a: Afr | null) : string {
        var result: string = "no name";
        if (a && a.taxonomyType) {
            switch (a.taxonomyType) {
                case TaxonomyTypeEnum.COURT_REPORTING_939:
                    result = "Court Reporting 939.185, F.S. (PDF)";
                    break;

                case TaxonomyTypeEnum.COURT_REPORTING_29:
                    result = "Court Reporting 29.0085, F.S. (PDF)";
                    break;

                default:
                    result = "Annual Financial Report (PDF)";
                    break;
            }
        }
        return result;
    }
   
    async function startReport(reportType: string, fileName: string, otype : string) {
        if (currentAfr !== null &&
            entity !== null) {
            const afrData: AFRDocumentStarter = {
                afrYear: currentAfr.year,
                entityCode: currentAfr.afrId,
                fileType: reportType,
                outputType: otype,
                populate: 'CURRENT_YEAR'
            };
            const response = await dispatch(getAFRReport(afrData));
            const doc = unwrapResult(response);
            if (doc != null) {

                //TODO make this a generic function in document slice
                //that takes mime type, base64 encoded string, filename
                //and returns a blob.
                //TODO expand to handle xhtml and excel files
                var url = "data:" + doc.mimeType + ";base64," + doc.content;
                await fetch(url)
                    .then(res => res.blob())
                    .then(otho => {

                        const data = window.URL.createObjectURL(otho);
                        var link = document.createElement("a");
                        link.href = data;
                        link.download = fileName; //"report.xhtml";
                        link.click();
                        setTimeout(function() {
                            // For Firefox it is necessary to delay revoking the ObjectURL
                            window.URL.revokeObjectURL(data);
                        }, 100);
                    });
            }

            return doc;
        }
    }
    const getEntityName = function (e: Entity | null, afr: Afr | null): string {
        if (!e) {
            return "";
        }
        if (!afr) {
            return e.name;
        }
        return getNameForAfr(e, afr);
    }
    const generateReportName = function (suffix: string): string {
        var result: string = "";
        if (!currentAfr) {
            return "no_afr" + suffix;
        }
        if (currentAfr && currentAfr.taxonomyType) {
            var afrYear = currentAfr.year.toString();
            switch (currentAfr.taxonomyType) {
                case TaxonomyTypeEnum.COURT_REPORTING_939:
                    var reportQuarter: string = currentAfr.logicalPeriod ? currentAfr.logicalPeriod.replace("Q_", "Q") : "ANNUAL";
                    result = "939 - " + getEntityName(entity, currentAfr) + " - " + reportQuarter + " - " + afrYear
                    break;

                default:
                    result = currentAfr.taxonomyType + " " + getEntityName(entity, currentAfr) + " " + afrYear
                    break;
            }

        }

        return result + suffix;
    }

    async function generateAFRReport() {
        setAfrXhtmlDownloadIsInProgress(true);
        try {
            if (currentAfr != null && entity != null) {
                await startReport("AFR", generateReportName(" - iXBRL.xhtml"), "IXBRL");
            }
        } catch (err) {
            console.log(err);
            setAfrXhtmlDownloadIsInProgress(false);
        }
        setAfrXhtmlDownloadIsInProgress(false);
    }

    async function generateAFRReportPDF() {
        setAfrPdfDownloadIsInProgress(true);
        try {
            if (currentAfr != null && entity != null) {
                await startReport("AFR", generateReportName(".pdf") , "PDF");
            }
        } catch (err) {
            console.log(err);
            setAfrPdfDownloadIsInProgress(false);
        }
        setAfrPdfDownloadIsInProgress(false);
    }

    async function generateSPFSReport() {
        setSpfsXhtmlDownloadIsInProgress(true);
        try {
            if (currentAfr != null && entity != null) {
                await startReport("SPFS", "SPFS " + entity.name + " " + currentAfr.year.toString() + ".xhtml","XHTML");
            }
        } catch (err) {
            console.log(err);
            setSpfsXhtmlDownloadIsInProgress(false);
        }
        setSpfsXhtmlDownloadIsInProgress(false);
    }

    async function generateSPFSReportPDF() {
        setSpfsPdfDownloadIsInProgress(true);
        try {
            if (currentAfr != null && entity != null) {
                await startReport("SPFS", "SPFS " + entity.name + " " + currentAfr.year.toString() + ".pdf", "PDF");
            }
        } catch (err) {
            console.log(err);
            setSpfsPdfDownloadIsInProgress(false);
        }
        setSpfsPdfDownloadIsInProgress(false);
    }

    // action to upload and process the file
    const handleUploadFile = async function(actionType: string, entityType: string, entityId: string, uploadedFile: File | undefined) {
        if (uploadedFile !== undefined) {
            if (entity && currentAfr) {
                let afrAuditDocument: { entityCode: string; afrYear: number; file: File }
                    = { entityCode: entity.code, afrYear: currentAfr.year, file: uploadedFile };

                await dispatch(uploadAuditDocument(afrAuditDocument));
                var AFRId = entity.code + "^" + currentAfr.year.toString();
                await dispatch(loadAfr(AFRId));
            }
        }
    }

    const isDocumentListSelectedAndLoading = (optionDocumentId: any) => {

        return documentListItemIsLoading && selectedDocumentListItem == optionDocumentId;
    }

    return (
        <React.Fragment>
            <Row>
                <Col md="10">
                    <Toast className="auditDetail">
                        <ToastHeader className="auditDetail">Documents</ToastHeader>
                        <ToastBody>
                            <div className="scrollable-table" style={{ height: "20em" }}>
                                <Table className="table" >
                                    <thead className="scrollable-table-header thead-dark">
                                        <tr>
                                            <th className="col-6">Name</th>
                                            <th className="col-2">Type</th>
                                            <th className="col-4">Date</th>
                                            {(!utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified))
                                                && <th className="col-1">Action</th>}

                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <div hidden={afrPdfDownloadIsInProgress == false}>
                                                    <Spinner children={''} style={{ width: '1rem', height: '1rem', marginRight: '1em' }}
                                                        type="grow" color="warning"
                                                        hidden={afrPdfDownloadIsInProgress == false} />
                                                    {getFinancialReportName(currentAfr)}
                                                </div>
                                                <div hidden={afrPdfDownloadIsInProgress == true}>
                                                    <a className="anchorButton" onClick={generateAFRReportPDF}>{getFinancialReportName(currentAfr)}</a>
                                                </div>
                                            </td>
                                            <td>Report</td>
                                            <td>{currentAfr != null ? currentAfr.year.toString() : ""}</td>
                                            {(!utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified))
                                                && <td></td>}
                                        </tr>
                                        {utilityHelper.isAfr(currentAfr)
                                            ?
                                            <tr>
                                                <td>
                                                    <div hidden={afrXhtmlDownloadIsInProgress == false}>
                                                        <Spinner children={''} style={{ width: '1rem', height: '1rem', marginRight: '1em' }}
                                                            type="grow" color="warning"
                                                            hidden={afrXhtmlDownloadIsInProgress == false} />
                                                        {"Annual Financial Report (iXBRL)"}
                                                    </div>
                                                    <div hidden={afrXhtmlDownloadIsInProgress == true}>
                                                        <a className="anchorButton" onClick={generateAFRReport}>Annual Financial Report (iXBRL)</a>
                                                    </div>
                                                </td>
                                                <td>Report</td>
                                                <td>{currentAfr != null ? currentAfr.year.toString() : ""}</td>
                                                {(!utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified))
                                                    && <td></td>}
                                            </tr>
                                            : <tr></tr>
                                        }
                                        {documentList.map((option, idx) => (
                                            <tr key={utilityHelper.generateGuid()}>
                                                {utilityHelper.isNullDocumentRelationship(option.document) ?
                                                    <td>No document</td>
                                                    :
                                                    <td>
                                                        <div hidden={isDocumentListSelectedAndLoading(option.document.documentId) == false}>
                                                            <Spinner children={''} style={{ width: '1rem', height: '1rem', marginRight: '1em' }}
                                                                type="grow" color="warning"
                                                                hidden={isDocumentListSelectedAndLoading(option.document.documentId) == false} />
                                                            {option.name}
                                                        </div>
                                                        <div hidden={isDocumentListSelectedAndLoading(option.document.documentId) == true}>
                                                            <a className="anchorButton" onClick={handleClickDownload(option.document.documentId)}>{option.name}</a>
                                                        </div>
                                                    </td>
                                                }
                                                <td>{utilityHelper.toTitleCase(option.type)}</td>
                                                <td>{dateHelper.stringDateInFormat(option.document.timestamp, "LL/dd/yyyy")}</td>
                                                {(!utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified))
                                                    && <td><a className="anchorButton" onClick={handleClickDelete(option.document.documentId, option.type, option.document.affiliate)}>Delete</a></td>}
                                            </tr>
                                        ))}

                                    </tbody>
                                </Table>
                            </div>
                             {props.taxonomyType == TaxonomyTypeEnum.AFR
                                ?
                                <div></div>
                                :
                                <Row>
                                    <Col>
                                        <ReportDataImporter userRole={props.userRole} taxonomyType={props.taxonomyType} importLabel={props.importLabel} downloadTemplateLabel={props.downloadTemplateLabel} />
                                    </Col>
                                </Row>
                            }
                            <Row hidden={hideDocUploader()}>
                                <Col>
                                    <AdminDocumentUploader taxonomyType={props.taxonomyType ? props.taxonomyType : TaxonomyTypeEnum.AFR} />
                                </Col>
                            </Row>
                       </ToastBody>
                    </Toast>
                </Col>
                <Col md="2">

                </Col>
            </Row>
        </React.Fragment>
    )

}

export default DocumentList;